// credit-card-vault.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectStore } from '@store/selectors';
import { map, switchMap, take } from 'rxjs/operators';
import { IResponseObjectSuccess } from '@core/interfaces';
import {
	VaultedCardResponse,
	CreateVaultedCardRequest,
	CardTokenResponse,
	VaultedCard,
	Store as StoreModel
} from '@core/models';
import { ICustomerData } from '@/features/checkout/components/credit-card-form/interfaces';

@Injectable({
	providedIn: 'root',
})
export class CreditCardVaultService {
	store$: Observable<StoreModel | null>;

	private closeFormSource = new Subject<void>();
	closeForm$ = this.closeFormSource.asObservable();

	constructor(
		private http: HttpClient,
		private store: Store
	) { 
		this.store$ = this.store.select(selectStore);
	}

	closeForm(): void {
    this.closeFormSource.next();
  }

	createTokenAssociatedCardGuest(token: string, customerData: ICustomerData): Observable<CardTokenResponse> {
		return this.http.post<CardTokenResponse>(`${environment.merchantMx}/customer`, customerData, {
			params: {
				token: encodeURIComponent(token),
			},
			headers: {
				'Content-Type': 'application/json',
				accept: 'application/json',
			},
		});
	}

	createTokenAssociatedCardLogged(token: string, customerData: ICustomerData, customerMerchId: string): Observable<CardTokenResponse> {
		return this.http.post<string>(`${environment.merchantMx}`, customerData, {
			params: {
				token: encodeURIComponent(token),
				customerid: encodeURIComponent(customerMerchId),
			},
			headers: {
				'Content-Type': 'application/json',
				accept: 'application/json',
			},
		})
			.pipe(
				map((response: string) => {
					return {
						id: Number(customerMerchId),
						cardAccount: {
							token: response,
							hasContract: true,
						},
					};
				})
			);
	}

	// Obtener todas las tarjetas
	getAllVaultedCards(limit: number = 10, offset: number = 0, clientId?: string): Observable<VaultedCardResponse> {
		return this.store$.pipe(
			take(1),
			switchMap(storeData => {
				const apiKey = storeData?.apiKey;
				if (!apiKey) {
					throw new Error('API key not available');
				}

				const params = {
					...(!!clientId && { clientId }),
					limit,
					offset
				};

				return this.http.get<VaultedCardResponse>(`${environment.microIntegrationMerchantMx}/vaulted-card`, {
					headers: {
						accept: 'application/json',
						'Content-Type': 'application/json',
						Authorization: apiKey,
					},
					params,
				});
			})
		);
	}

	// Obtener una tarjeta específica
	getVaultedCardById(idVaultedCard: string): Observable<VaultedCardResponse> {
		return this.store$
			.pipe(
			take(1),
			switchMap(storeData => {
				const apiKey = storeData?.apiKey;
				if (!apiKey) {
					throw new Error('API key not available');
				}

				return this.http.get<VaultedCardResponse>(
					`${environment.microIntegrationMerchantMx}/vaulted-card/${idVaultedCard}`,
					{
						headers: {
							accept: 'application/json',
							'Content-Type': 'application/json',
							Authorization: apiKey,
						},
					}
				);
			})
		);
	}

	// Crear una nueva tarjeta
	createVaultedCard(request: CreateVaultedCardRequest): Observable<IResponseObjectSuccess<VaultedCard>> {
		return this.store$
			.pipe(
			take(1),
			switchMap(storeData => {
				const apiKey = storeData?.apiKey;
				if (!apiKey) {
					throw new Error('API key not available');
				}

				return this.http.post<IResponseObjectSuccess<VaultedCard>>(
					`${environment.microIntegrationMerchantMx}/vaulted-card`,
					request,
					{
						headers: {
							accept: 'application/json',
							'Content-Type': 'application/json',
							Authorization: apiKey,
						},
					}
				);
			})
		);
	}

	// Eliminar una tarjeta
	deleteVaultedCard(id: string): Observable<{ success: boolean; message: string }> {
		return this.store$
			.pipe(
			take(1),
			switchMap(storeData => {
				const apiKey = storeData?.apiKey;
				if (!apiKey) {
					throw new Error('API key not available');
				}

				return this.http.delete<{ success: boolean; message: string }>(
					`${environment.microIntegrationMerchantMx}/vaulted-card/${id}`,
					{
						headers: {
							accept: 'application/json',
							'Content-Type': 'application/json',
							Authorization: apiKey,
						},
					}
				);
			})
		);
	}

	// Establecer tarjeta como predeterminada
	setDefaultVaultedCard(idVaultedCard: string, isDefault: boolean): Observable<{ success: boolean; message: string }> {
		return this.store$.pipe(
			take(1),
			switchMap(storeData => {
				const apiKey = storeData?.apiKey;
				if (!apiKey) {
					throw new Error('API key not available');
				}

				return this.http.patch<{ success: boolean; message: string }>(
					`${environment.microIntegrationMerchantMx}/vaulted-card/${idVaultedCard}/set-default`,
					{ isDefault },
					{
						headers: {
							accept: 'application/json',
							'Content-Type': 'application/json',
							Authorization: apiKey,
						}
					}
				);
			})
		);
	}
}
